import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { ActivityTypesEnum } from 'src/app/shared/interfaces/ActivityTypesEnum';
import { BackCountActivityList } from 'src/app/shared/interfaces/back-count-activity-list';
import { BadgeColorsEnum } from 'src/app/shared/interfaces/BadgeColorsEnum';
import { ICheckBoxGroupOption, ICheckBoxGroupOptionItem } from 'src/app/shared/interfaces/checkbox-group-option';
import { DrugScheduleEnum, DrugScheduleFilterEnum } from 'src/app/shared/interfaces/DrugScheduleEnum ';
import { PinService } from 'src/app/shared/services/pin.service';
import { ToggleBackCount, UpdateBackCount } from 'src/app/shared/states/pharmacy/pharmacy.actions';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { IRequiredBackCountActivityModel } from '../../interfaces/required-back-count-activity-model.interface';
import { ISaveBackCountRequest } from '../../interfaces/save-back-count-request-interface';
import { AlertMessageService } from 'src/app/shared/services/alert-message.service';
import { AlertTypesEnum } from 'src/app/shared/interfaces/AlertTypesEnum';

@UntilDestroy()
@Component({
  selector: 'app-back-count',
  templateUrl: './back-count.component.html',
  styleUrls: ['./back-count.component.scss', '../../settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BackCountComponent implements OnInit {
  
  BadgeColorsEnum = BadgeColorsEnum;
  
  @Select(PharmacyState.backCountEnabled) backCountEnabled$: Observable<boolean>;
  @Select(PharmacyState.backCountValue) backCountValue$: Observable<IRequiredBackCountActivityModel[]>;


  backCountEnabled: boolean;

  displaySelectActivitiesDialog: boolean;

  activityWithSchedulesGroups: ICheckBoxGroupOption[] = [];

  selectedActivitiesString: string;

  updateOperation: boolean;

  private togglePinChannel = 'toggleBackCount';
  private updatePinChannel = 'updateBackCount';

  /**
   *
   */
  constructor(
    private store: Store, 
    private pinService: PinService, 
    private cdr: ChangeDetectorRef, 
    private alertService: AlertMessageService
  ) { }

  ngOnInit(): void {
    this.backCountEnabled$
    .pipe(untilDestroyed(this))
    .subscribe((val) => this.backCountEnabled = val);

    this.backCountValue$
    .pipe(untilDestroyed(this))
    .subscribe((val) => {
      if (val) {
        this.selectedActivitiesString = val?.map((activity) => {
        const activityName = BackCountActivityList.find(x => x.id === activity.ActivityType)?.name;
        return activityName;
        }).filter(name => !!name).join(', ');
      }
    })

    this.pinService.validatePin$
    .pipe(untilDestroyed(this))
    .subscribe(res => {
      if (res?.success && res.dialogId === this.togglePinChannel) {
        this.toggleSettingRequest(res.pin);
      } else if (res?.success && res.dialogId === this.updatePinChannel) {
        this.updateSettingValueRequest(res.pin);
      }
    });
  }

  onSwitchClick(): void {
    if (!this.backCountEnabled) {
      this.setBackCountActivityList();
      this.displaySelectActivitiesDialog = true;
    } else {
      this.openPinDialog(this.togglePinChannel);
    }
  }

  openPinDialog(channel: string): void {
    this.displaySelectActivitiesDialog = false;
    this.pinService.openPinDialog(channel);
  }

  editBackCountConfigurationClicked(): void {
    this.setBackCountActivityList();
    this.updateOperation = true; 
    this.displaySelectActivitiesDialog = true;
  }

  public saveSelectedActivities(): void {
    if (!this.activityWithSchedulesGroups?.filter(x => x.groupHeaderItem.checked)?.length) {
      this.alertService.displayAlert('Should select at least one activity.', AlertTypesEnum.Error);
      return;
    }
    if (this.activityWithSchedulesGroups?.some(x => x.groupHeaderItem.checked && x.groupItems.every(y => !y.checked))) {
      this.alertService.displayAlert('Should select at least one schedule per activity.', AlertTypesEnum.Error);
      return;
    }
    this.updateOperation ? this.openPinDialog(this.updatePinChannel) : this.openPinDialog(this.togglePinChannel);
  }

  private toggleSettingRequest(pin: number): void {
    const request: ISaveBackCountRequest = {
      pin,
      activities: this.getActivitiesPayload(),
    };

    this.store.dispatch(new ToggleBackCount(request))
    .pipe(untilDestroyed(this))
    .subscribe(() => {
      this.afterSettingSavedHandler();
    })
  }

  private updateSettingValueRequest(pin: number): void {
    const request: ISaveBackCountRequest = {
      pin,
      activities: this.getActivitiesPayload(),
    };

    this.store.dispatch(new UpdateBackCount(request))
    .pipe(untilDestroyed(this))
    .subscribe(() => {
      this.afterSettingSavedHandler();
    })
  }

  private afterSettingSavedHandler(): void {
    this.pinService.closeDialog();
      this.updateOperation = false;
      this.cdr.markForCheck();
  }

  private getActivitiesPayload(): IRequiredBackCountActivityModel[] {
    return this.activityWithSchedulesGroups.filter(x => x.groupHeaderItem.checked).map(activityWithSchedule => ({
      ActivityType: activityWithSchedule.groupHeaderItem.id,
      Schedules: activityWithSchedule.groupItems.filter(schedule => schedule.checked)?.map(x => x.id)
    }) as any);
  }

  private setBackCountActivityList(): void {
  this.activityWithSchedulesGroups = [];
  const backCountValue = this.store.selectSnapshot(PharmacyState.backCountValue);
    BackCountActivityList.map(activity => {
      const idxOfCheckedActivity = backCountValue?.findIndex(x => x.ActivityType == activity.id) ?? -1;
      this.activityWithSchedulesGroups.push(
        {
          groupHeaderItem: {
            id: activity.id,
            name: activity.name,
            checked: idxOfCheckedActivity > -1
          },
          groupItems: this.getEnabledSchedules(backCountValue?.[idxOfCheckedActivity]?.Schedules || [])
        })
    });
  }

  private getEnabledSchedules(checkedSchedules: DrugScheduleEnum[]): ICheckBoxGroupOptionItem[] {
    const isChecked = (schedule: DrugScheduleEnum) => checkedSchedules.includes(schedule);
    let schedules = [{id: DrugScheduleEnum.C2, name: DrugScheduleFilterEnum[DrugScheduleEnum.C2], checked: isChecked(DrugScheduleEnum.C2)}];
    if (this.store.selectSnapshot(PharmacyState.enableC3toC5Drugs)) {
      schedules = [...schedules, ...[
        {id: DrugScheduleEnum.C3, name: DrugScheduleFilterEnum[DrugScheduleEnum.C3], checked: isChecked(DrugScheduleEnum.C3)},
        {id: DrugScheduleEnum.C4, name: DrugScheduleFilterEnum[DrugScheduleEnum.C4], checked: isChecked(DrugScheduleEnum.C4)},
        {id: DrugScheduleEnum.C5, name: DrugScheduleFilterEnum[DrugScheduleEnum.C5], checked: isChecked(DrugScheduleEnum.C5)},
        {id: DrugScheduleEnum.MISC, name: DrugScheduleFilterEnum[DrugScheduleEnum.MISC], checked: isChecked(DrugScheduleEnum.MISC)}
      ]]
    }
    if (this.store.selectSnapshot(PharmacyState.compoundingEnabled)) {
      schedules = [...schedules,  {id: DrugScheduleEnum.Compound, name: DrugScheduleFilterEnum[DrugScheduleEnum.Compound], checked: isChecked(DrugScheduleEnum.Compound)}]
    }
    return schedules;
  }

}
